import { ElementProps } from '@/components/sections/builder/types';
import {
  ImageBannerElementDataType,
  ImageBannerSubtitleElementDataType,
} from '@/components/sections/builder/elements/body/image-banner/types';
import InputTab from '@/components/sections/builder/settings/input-tab';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';
import { Type } from 'lucide-react';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';

const defaultData: ImageBannerSubtitleElementDataType = {
  name: 'Sub Title',
  label: 'Give customers details about the banner image(s) or content on the template.',
  textStyle: 'subtitle',
  padding: {
    bottom: 16,
    top: 0,
    left: 0,
    right: 0,
  },
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as ImageBannerSubtitleElementDataType;
  const parentData = element.parentData as ImageBannerElementDataType;

  return (
    <div className='break-words'>
      <p
        className={clsx(
          'text_color_container inline-block w-full text-left text-gray-500 lg:max-w-[672px]',
          data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
          data.textStyle === 'subtitle' && 'text-sm md:text-base lg:text-lg',
          data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base',
          parentData.contentAlign === 'left' && 'md:text-left',
          parentData.contentAlign === 'center' && 'md:text-center',
          parentData.contentAlign === 'right' && 'md:text-right',
          parentData.contentAlignMobile === 'left' && 'text-left',
          parentData.contentAlignMobile === 'center' && 'text-center',
          parentData.contentAlignMobile === 'right' && 'text-right'
        )}
        data-animation='true'
        dangerouslySetInnerHTML={{ __html: data.label }}
      />
      <style jsx>
        {`
          @media (min-width: 1024px) {
            .subtitle-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }

          .text_color_container {
            color: ${parentData.containerColorScheme?.text || '#6b7280'};
          }
        `}
      </style>
    </div>
  );
};

export const ElementPreviewIcon = Type;
export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ImageBannerSubtitleElementDataType;

  return (
    <div>
      <div className='mb-4 font-bold'>Text</div>
      <TextEditorMinimal
        placeholder='Label'
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            label: value,
          })
        }
        value={data.label}
        className='mb-4 w-full '
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-4 mt-4 font-bold'>Text Style</div>
      <InputTab
        defaultValue={data.textStyle}
        options={[
          { label: 'Body', value: 'body' },
          { label: 'Subtitle', value: 'subtitle' },
          { label: 'UPPERCASE', value: 'uppercase' },
        ]}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            textStyle: v,
          })
        }
      />
    </div>
  );
}

const ImageBannerSubtitle = {
  type: 'image_banner_subtitle',
  group: 'image_banner',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ImageBannerSubtitle;
